/**
 * Wire (snake_case from v3 API) → frontend Booking (camelCase) mapper.
 * Copied 1:1 from v3 hooks/mapBooking.ts.
 *
 * Important: PII fields (`client_name`, `phone`, `telegram`) arrive already
 * decrypted from the worker — the worker holds ENCRYPTION_KEY and runs
 * decryptRows() before serialising. So this mapper sees plaintext.
 */

import type { Booking } from "./bookingTypes";

export interface RawBooking {
  id: number;
  quest_id: string;
  time: string;
  type: "booking" | "lead" | "block";
  players: number;
  client_name: string;
  phone: string;
  telegram: string;
  tea: boolean | number;
  tea_minutes: number;
  tea_room_id: string | null;
  mc: string;
  prepaid: boolean | number;
  prepaid_amount: number;
  comment: string;
  source: string;
  dc_user_id: string;
  package: string;
  expected_revenue: number;
  actual_revenue: number;
  blocked_by: string;
  blocked_by_name?: string | null;
  block_reason: string;
  created_by_name: string;
  created_by: string;
  created_at: string;
  modified_by_name: string;
  modified_by: string;
  modified_at: string;
  is_class: boolean | number;
  is_birthday: boolean | number;
  is_learning?: boolean | number;
  is_competition?: boolean | number;
  is_special_event?: boolean | number;
  special_version_id?: string | null;
  /** Название версии с сервера (join без active/archived) — имя игры для всех экранов. */
  special_version_title?: string | null;
  is_priority?: boolean | number;
  channel?: "tg" | "vk" | "max" | null;
  custom_time: string | null;
  date: string;
}

export function mapBooking(raw: RawBooking, nightSlot: boolean): Booking {
  return {
    id: raw.id,
    questId: raw.quest_id,
    time: raw.time,
    type: raw.type,
    players: raw.players || 0,
    clientName: raw.client_name || "",
    phone: raw.phone || "",
    tg: raw.telegram || "",
    tea: !!raw.tea,
    teaMin: raw.tea_minutes || 0,
    teaRoomId: raw.tea_room_id || null,
    mc: raw.mc || "none",
    prepaid: !!raw.prepaid,
    prepaidAmount: raw.prepaid_amount || 0,
    comment: raw.comment || "",
    source: raw.source || "",
    dkUser: raw.dc_user_id || "",
    package: raw.package || "стандарт",
    expectedRevenue: raw.expected_revenue || 0,
    actualRevenue: raw.actual_revenue || 0,
    blockedBy: raw.blocked_by_name || raw.blocked_by || "",   // 28.09: имя, если это сотрудник
    blockReason: raw.block_reason || "",
    createdBy: raw.created_by_name || raw.created_by || "",
    createdAt: raw.created_at || "",
    modifiedBy: raw.modified_by_name || raw.modified_by || "",
    modifiedAt: raw.modified_at || "",
    isClass: !!raw.is_class,
    isBirthday: !!raw.is_birthday,
    isLearning: !!raw.is_learning,
    isCompetition: !!raw.is_competition,
    /* 22.09: признак СпцВ — флаг ИЛИ привязанная версия. Заявки из приложения
       приходят с версией, но без флага (routes/client.ts его не пишет) — старое чтение
       гасило тумблер в модалке, и ЛЮБОЕ сохранение стирало привязку к версии
       (savePayload: special_version_id шлётся только при включённом флаге). */
    isSpecialEvent: !!raw.is_special_event || !!(raw.special_version_id ?? "").toString().trim(),
    specialVersionId: (raw.special_version_id as string) || "",
    specialVersionTitle: (raw.special_version_title as string) || "",
    isPriority: !!raw.is_priority,
    channel: (raw.channel ?? "") as "" | "tg" | "vk" | "max",
    customTime: raw.custom_time || null,
    _nightSlot: nightSlot,
    _realDate: raw.date || "",
  };
}
